import type { IconName } from '@hotjar/hotsauce';
import { navigateToPermissionRequiredPage } from '@hotjar/recordings/src/features/Playbacks/utils';
import { getLoggedIn } from '@hotjar/webapp/domains/common/user-data';
import {
  isDemoSiteId,
  redirectToDemoLogin,
} from '@hotjar/webapp/domains/acquisition/demo/demo-site-redirections';
import type { Recording } from '../types';
import type { PlaybacksError } from '../store/iframe-controller';
import { getSiteId } from '../store/utils';

type PlaybacksErrorPageData = {
  icon: IconName;
  title: string;
  subtitle: string;
  buttonIcon?: IconName;
  buttonText: string;
  onButtonClick: () => unknown;
  recording?: Recording;
  isLoggedIn?: boolean;
};

const goToInsights = (): void => {
  window.location.href = '/';
};

enum PlaybacksErrorKey {
  DELETED_RECORDING_SIGNED_IN = 'deletead-recording-signed-in',
  DELETED_RECORDING_SIGNED_OUT = 'deleted-recording-signed-out',
  DEFAULT_SIGNED_IN = 'default-signed-in',
  DEFAULT_SIGNED_OUT = 'default-signed-out',
}

const baseErrorPageData: { icon: IconName; onButtonClick: typeof goToInsights } = {
  icon: 'warning_alt',
  onButtonClick: goToInsights,
};

const playbacksErrorPageData: { [key in PlaybacksErrorKey]: PlaybacksErrorPageData } = {
  [PlaybacksErrorKey.DELETED_RECORDING_SIGNED_IN]: {
    ...baseErrorPageData,
    title: 'common:recordings.player.error-page.deleted-recording-signed-in.title',
    subtitle: 'common:recordings.player.error-page.deleted-recording-signed-in.subtitle',
    buttonText: 'common:recordings.player.error-page.deleted-recording-signed-in.buttonText',
  },
  [PlaybacksErrorKey.DELETED_RECORDING_SIGNED_OUT]: {
    ...baseErrorPageData,
    title: 'common:recordings.player.error-page.deleted-recording-signed-out.title',
    subtitle: 'common:recordings.player.error-page.deleted-recording-signed-out.subtitle',
    buttonText: 'common:recordings.player.error-page.deleted-recording-signed-out.buttonText',
  },
  [PlaybacksErrorKey.DEFAULT_SIGNED_IN]: {
    ...baseErrorPageData,
    title: 'common:recordings.player.error-page.default-signed-in.title',
    subtitle: 'common:recordings.player.error-page.default-signed-in.subtitle',
    buttonText: 'common:recordings.player.error-page.default-signed-in.buttonText',
  },
  [PlaybacksErrorKey.DEFAULT_SIGNED_OUT]: {
    ...baseErrorPageData,
    title: 'common:recordings.player.error-page.default-signed-out.title',
    subtitle: 'common:recordings.player.error-page.default-signed-out.subtitle',
    buttonText: 'common:recordings.player.error-page.default-signed-out.buttonText',
  },
};

const isErrorPlaybacksApiError = (error: PlaybacksError) => {
  return !!(
    error &&
    typeof error === 'object' &&
    Object.prototype.hasOwnProperty.call(error, 'response')
  );
};

const shouldHandleErrorWithRedirect = (error: PlaybacksError): boolean => {
  const status = error.response?.status;
  const redirectScenarios = [401, 403, 451];
  return !!(isErrorPlaybacksApiError(error) && status && redirectScenarios.includes(status));
};

const handleErrorWithRedirect = (error: PlaybacksError): void => {
  const status = error.response?.status;
  const siteId = getSiteId();

  if (status === 451) {
    window.location.href = 'https://www.hotjar.com/451';
  }

  if (status === 401 && isDemoSiteId(siteId)) {
    redirectToDemoLogin();
  }

  navigateToPermissionRequiredPage(siteId);
};

const generatePlaybacksErrorData = (error: PlaybacksError): PlaybacksErrorPageData => {
  if (isErrorPlaybacksApiError(error)) {
    const status = error.response?.status;

    if (status === 404) {
      return getLoggedIn()
        ? playbacksErrorPageData[PlaybacksErrorKey.DELETED_RECORDING_SIGNED_IN]
        : playbacksErrorPageData[PlaybacksErrorKey.DELETED_RECORDING_SIGNED_OUT];
    }
  }

  return getLoggedIn()
    ? playbacksErrorPageData[PlaybacksErrorKey.DEFAULT_SIGNED_IN]
    : playbacksErrorPageData[PlaybacksErrorKey.DEFAULT_SIGNED_OUT];
};

export { generatePlaybacksErrorData, shouldHandleErrorWithRedirect, handleErrorWithRedirect };

export type { PlaybacksErrorPageData };
